import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { SIDEBAR_CONFIG } from "../Data/sidebarConfig";
import { API_BASE } from "../utils/api";
import {
  UilAngleDown,
  UilAngleUp,
} from "@iconscout/react-unicons";
import { getAppConfig } from "../utils/appConfig";
import { getSidebarByRole } from "../Data/Data";
import { useRestaurantDetails } from "../context/RestaurantDetailsContext";


const Sidebar = ({ collapsed, setCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 768;
  const [openDropdown, setOpenDropdown] = useState(null);
  const [collapsedSubmenu, setCollapsedSubmenu] = useState(null);
  const [submenuPosition, setSubmenuPosition] = useState({ top: 0, left: 0 });
  const menuItemRefs = useRef({});
  const role = localStorage.getItem("role");
  const { defaultImage, logo } = getAppConfig();
  const { restaurantDetails } = useRestaurantDetails();

// IMPORTANT (based on your current context)
const features = restaurantDetails?.restaurant?.features || {};
const sidebarPermissions = restaurantDetails?.restaurant?.sidebarPermissions || null;

  const filteredSidebarData = getSidebarByRole(role, features, sidebarPermissions);

  useEffect(() => {
    // Removed mobile-specific logic to allow Layout component to control sidebar state
  }, [location.pathname, setCollapsed, isMobile]);

  const sidebarVariants = {
    true: { left: "0" },
    false: { left: "-70%" },
  };

  const handleItemClick = (link) => {
    navigate(link);
    setCollapsedSubmenu(null); // Close collapsed submenu after navigation
    if (isMobile) setCollapsed(true); // Close sidebar after navigation on mobile
  };

  const toggleDropdown = (index) => {
    setOpenDropdown(openDropdown === index ? null : index);
  };

  const toggleCollapsedSubmenu = (index) => {
    if (collapsedSubmenu === index) {
      setCollapsedSubmenu(null);
    } else {
      const menuItem = menuItemRefs.current[index];
      if (menuItem) {
        const rect = menuItem.getBoundingClientRect();
        setSubmenuPosition({
          top: rect.top,
          left: rect.right + 8,
        });
      }
      setCollapsedSubmenu(index);
    }
  };

  return (
    <motion.div
      className={`
        flex flex-col h-full pt-2 font-bold transition-all duration-300 ease-in-out z-[1000]
        bg-white shadow-lg
        ${isMobile && collapsed ? "-translate-x-full" : ""}
        ${collapsed ? "items-center px-2" : "px-0"}
      `}
      variants={sidebarVariants}
      animate={isMobile ? "" : ""}
    >
      {/* Sidebar Header */}
      <div
        className={`relative flex items-center justify-center py-3.5 ${collapsed ? "px-2" : "px-5"}`}
      >
        {collapsed ? (
          <img
            src={logo ? `${API_BASE}${logo}` : "/img/finglider_logo.jpg"}
            alt="Logo"
            className="h-8 w-8 object-contain"
            onError={(e) => (e.target.src = "/img/finglider_logo.jpg")}
          />
        ) : (
          <img
            src={logo ? `${API_BASE}${logo}` : "/img/finglider_logo.jpg"}
            alt="Logo"
            className="h-[52px] w-auto object-contain"
            onError={(e) => (e.target.src = "/img/finglider_logo.jpg")}
          />

        )}
        {isMobile && (
          <div
            className="absolute top-3.5 right-4 cursor-pointer text-[var(--color-sidebar-text)] md:hidden w-6 h-6 flex items-center justify-center"
            onClick={() => setCollapsed(true)}
          >
            ✕
          </div>
        )}
      </div>

      {/* Menu */}
      <div
        className={`mt-8 flex flex-col gap-1.5 font-text bg-white overflow-y-auto overflow-x-hidden ${collapsed ? "items-center" : ""}`}
        style={{ flex: 1 }}
      >
        {filteredSidebarData.map((item, index) => {
          const hasSubmenu = item.submenu && item.submenu.length > 0;
          const isActive = location.pathname.startsWith(item.link);

          return (
            <div key={index} className="relative group">
              {/* Menu Item */}
              <div
                ref={(el) => (menuItemRefs.current[index] = el)}
                className={`
                  flex items-center gap-4 py-2.5 rounded-xl transition-colors duration-150 text-base cursor-pointer
                  ${collapsed && isActive ? "px-2 mx-2 justify-center !bg-gradient-to-r !from-gray-900 !to-black !font-bold !shadow-lg !text-white" : ""}
                  ${collapsed && !isActive ? "px-2 mx-2 justify-center text-gray-700 hover:bg-gray-50" : ""}
                  ${!collapsed && isActive ? "px-6 mx-3 !bg-gradient-to-r !from-gray-900 !to-black !font-bold !shadow-lg !text-white" : ""}
                  ${!collapsed && !isActive ? "px-6 mx-3 text-gray-700 hover:bg-gray-50" : ""}
                `}
                onClick={() => {
                  if (hasSubmenu) {
                    if (collapsed) {
                      toggleCollapsedSubmenu(index);
                    } else {
                      toggleDropdown(index);
                    }
                  } else {
                    handleItemClick(item.link);
                  }
                }}
                title={collapsed ? item.heading : ""}
              >
                <item.icon className="w-[22px] h-[22px] flex-shrink-0" />
                {!collapsed && (
                  <>
                    <span className="flex-1">{item.heading}</span>
                    {hasSubmenu && (
                      <div className="dropdown-icon">
                        {openDropdown === index ? (
                          <UilAngleUp className="w-4 h-4" />
                        ) : (
                          <UilAngleDown className="w-4 h-4" />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Tooltip/Submenu for collapsed state */}
              {collapsed && !hasSubmenu && (
                <div
                  className="absolute left-full ml-2 top-1/2 transform -translate-y-1/2 
                              bg-gray-800 text-white text-sm px-2 py-1 rounded-md opacity-0 
                              group-hover:opacity-100 transition-opacity duration-200 pointer-events-none 
                              whitespace-nowrap z-50"
                  style={{
                    position: "absolute",
                    zIndex: 50,
                  }}
                >
                  {item.heading}
                </div>
              )}

              {/* Submenu popup for collapsed state - using portal */}
              {collapsed &&
                hasSubmenu &&
                collapsedSubmenu === index &&
                createPortal(
                  <div
                    className="bg-white text-gray-800 rounded-lg shadow-xl 
                              whitespace-nowrap z-[9999] py-2 min-w-[180px]
                              border border-gray-200"
                    style={{
                      position: "fixed",
                      top: `${submenuPosition.top}px`,
                      left: `${submenuPosition.left}px`,
                      zIndex: 9999,
                    }}
                  >
                    <div className="px-3 py-2 text-xs font-bold text-gray-500 border-b border-gray-200">
                      {item.heading}
                    </div>
                    {item.submenu.map((sub, idx) =>
                      sub.isGroupLabel ? (
                        <div
                          key={`${index}-${idx}`}
                          className="px-4 pt-3 pb-1 text-[11px] font-bold text-gray-400 uppercase tracking-wide"
                        >
                          {sub.heading}
                        </div>
                      ) : (
                        <div
                          key={`${index}-${idx}`}
                          className={`
                        px-4 py-2 text-sm cursor-pointer transition-all duration-200
                        hover:bg-gray-50
                        ${location.pathname.startsWith(sub.link) ? "bg-gradient-to-r from-gray-900 to-black font-bold text-white" : "text-gray-700"}
                      `}
                          onClick={() => handleItemClick(sub.link)}
                        >
                          {sub.heading}
                        </div>
                      ),
                    )}
                  </div>,
                  document.body,
                )}

              {/* Submenu - Hide when collapsed */}
              {hasSubmenu && openDropdown === index && !collapsed && (
                <div className="flex flex-col pl-10 mt-0.5">
                  {item.submenu.map((sub, idx) =>
                    sub.isGroupLabel ? (
                      <div
                        key={`${index}-${idx}`}
                        className="pt-3 pb-1 px-6 mx-3 text-[11px] font-bold text-gray-400 uppercase tracking-wide"
                      >
                        {sub.heading}
                      </div>
                    ) : (
                      <div
                        key={`${index}-${idx}`}
                        className={`
                        py-2.5 text-[15px] rounded-xl cursor-pointer transition-colors duration-150
                        ${location.pathname.startsWith(sub.link) ? "!bg-gradient-to-r !from-gray-900 !to-black !font-bold !shadow-lg !text-white !px-6 !mx-3" : "text-gray-700 hover:bg-gray-50 px-6 mx-3"}
                      `}
                        onClick={() => handleItemClick(sub.link)}
                      >
                        <span>{sub.heading}</span>
                      </div>
                    ),
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

    </motion.div>
  );
};

export default Sidebar;
